// ABOUTME: Admin behavior management component displaying all behavior logs across all families
// ABOUTME: Provides searchable, sortable, paginated view with admin editing capabilities

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  TextField,
  CircularProgress,
  Alert,
  Container,
  Button,
  Chip,
  IconButton,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip
} from '@mui/material';
import {
  ArrowUpward,
  ArrowDownward,
  ArrowBack,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Restore as RestoreIcon
} from '@mui/icons-material';
import { getBehaviorIcon, getBehaviorLabel, getBehaviorColor, getRecordedByDisplayName } from '../utils/behaviorUtils';
import {
  useReactTable,
  getCoreRowModel,
  createColumnHelper,
  flexRender,
  SortingState,
  ColumnFiltersState,
  getPaginationRowModel
} from '@tanstack/react-table';
import * as api from '../services/api';
import { BehaviorType } from '../types/api';
import SoftDeleteToggle, { SoftDeleteFilter } from './SoftDeleteToggle';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

interface AdminBehaviorRecord {
  behaviorId: number;
  childId: number;
  childName: string;
  familyId: number;
  familyName: string;
  behaviorType: number;
  notes: string | null;
  createdAt: string;
  recordedByUserId: number;
  isDeleted: boolean;
  recordedByUserName: string;
  source?: number;             // BehaviorRecordingSource enum value (matches API response)
  sourceDisplayName?: string;  // Human-readable source name (matches API response)
  
  // Enhanced time storage (Issue #312) - timeline stability fields
  eventTime: string;
  eventTimezone: string;
  eventLocalTime: string;
}


const AdminBehaviorManagement: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [data, setData] = useState<AdminBehaviorRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'createdAt', desc: true }
  ]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 25,
  });
  const [softDeleteFilter, setSoftDeleteFilter] = useState<SoftDeleteFilter>('active');

  // Modal states
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedBehavior, setSelectedBehavior] = useState<AdminBehaviorRecord | null>(null);
  const [editData, setEditData] = useState({
    behaviorType: 1,
    notes: '',
  });

  const columnHelper = createColumnHelper<AdminBehaviorRecord>();

  const columns = useMemo(
    () => [
      columnHelper.accessor('createdAt', {
        header: 'Date/Time',
        cell: (info) => {
          const behavior = info.row.original;
          
          // Helper function to get the best display time (following EventLog pattern)
          const getDisplayTime = (behavior: AdminBehaviorRecord) => {
            // Use eventLocalTime first (what user saw), then fallback to createdAt
            return behavior.eventLocalTime || behavior.createdAt;
          };
          
          const displayTime = getDisplayTime(behavior);
          
          // Create tooltip with enhanced time field details for admin debugging
          const timeTooltip = behavior.eventTime ? 
            `Enhanced Time Info:
EventTime: ${behavior.eventTime}
EventTimezone: ${behavior.eventTimezone}
EventLocalTime: ${behavior.eventLocalTime}
Legacy: CreatedAt: ${behavior.createdAt}
` :
            `Legacy Time Info:
CreatedAt: ${behavior.createdAt}`;
          
          // Safely format the date with NO timezone conversion - admin sees exact API values
          const formatSafely = (dateString: string) => {
            if (!dateString) return 'No date';
            
            // Parse ISO datetime string directly without any Date object to avoid timezone conversion
            const match = dateString.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/);
            if (match) {
              const [, year, month, day, hour, minute] = match;
              const hour24 = parseInt(hour);
              const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
              const ampm = hour24 >= 12 ? 'PM' : 'AM';
              return `${month}/${day}/${year} ${hour12}:${minute} ${ampm}`;
            }
            
            // If no match, return the raw string to avoid any Date conversion
            return `Raw: ${dateString}`;
          };
          
          return (
            <Tooltip title={timeTooltip} arrow>
              <Box component="span" sx={{ borderBottom: `1px dotted ${theme.palette.grey[500]}`, cursor: 'help' }}>
                {formatSafely(displayTime)}
              </Box>
            </Tooltip>
          );
        },
        enableColumnFilter: false
      }),
      columnHelper.accessor('childName', {
        header: 'Child',
        cell: (info) => {
          const behavior = info.row.original;
          const isDeleted = behavior.isDeleted;
          const isGreyedOut = softDeleteFilter === 'all' && isDeleted;
          
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                component="span"
                sx={{
                  color: isGreyedOut ? theme.palette.grey[500] : theme.palette.info.main,
                  fontWeight: 600,
                  textDecoration: isDeleted && softDeleteFilter === 'all' ? 'line-through' : 'none'
                }}
              >
                {info.getValue()}
              </Box>
              {isDeleted && (
                <Chip 
                  label="DELETED" 
                  size="small" 
                  color="error" 
                  variant="outlined"
                  sx={{ fontSize: '0.7rem', height: '20px' }}
                />
              )}
            </Box>
          );
        },
        meta: {
          filterPlaceholder: 'Search child name...'
        }
      }),
      columnHelper.accessor('familyName', {
        header: 'Family Name',
        cell: (info) => info.getValue(),
        meta: {
          filterPlaceholder: 'Search family name...'
        }
      }),
      columnHelper.accessor('behaviorType', {
        header: 'Behavior',
        cell: (info) => {
          const type = info.getValue();
          return (
            <Chip
              icon={getBehaviorIcon(type)}
              label={getBehaviorLabel(type)}
              size="small"
              sx={{
                backgroundColor: getBehaviorColor(type),
                color: 'white',
                '& .MuiChip-label': { color: 'white' }
              }}
            />
          );
        },
        enableColumnFilter: false
      }),
      columnHelper.accessor('recordedByUserName', {
        header: 'Who Logged',
        cell: (info) => getRecordedByDisplayName(info.row.original),
        enableColumnFilter: false
      }),
      columnHelper.accessor('notes', {
        header: 'Notes',
        cell: (info) => info.getValue() || '-',
        meta: {
          filterPlaceholder: 'Search notes...'
        }
      }),
      columnHelper.accessor('sourceDisplayName', {
        header: 'Source',
        cell: (info) => (
          <Chip
            label={info.getValue() || 'Unknown'}
            size="small"
            variant="outlined"
            color="default"
            sx={{ fontSize: '0.75rem' }}
          />
        ),
        enableColumnFilter: false
      }),
      columnHelper.display({
        id: 'actions',
        header: 'Actions',
        cell: (info) => {
          const behavior = info.row.original;
          const isDeleted = behavior.isDeleted;
          
          return (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton
                size="small"
                onClick={() => handleEdit(behavior)}
                data-testid="edit-behavior-button"
              >
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => isDeleted ? handleRestore(behavior) : handleDelete(behavior)}
                data-testid={isDeleted ? "restore-behavior-button" : "delete-behavior-button"}
                title={isDeleted ? "Restore behavior (Coming Soon)" : "Delete behavior"}
              >
                {isDeleted ? <RestoreIcon fontSize="small" /> : <DeleteIcon fontSize="small" />}
              </IconButton>
            </Box>
          );
        },
      }),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps -- columnHelper and softDeleteFilter are stable references created once per mount; adding them would recreate columns on every render
    []
  );

  const fetchBehaviors = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Build filter params
      const familyNameFilter = columnFilters.find(f => f.id === 'familyName')?.value as string;
      const childNameFilter = columnFilters.find(f => f.id === 'childName')?.value as string;
      const notesFilter = columnFilters.find(f => f.id === 'notes')?.value as string;

      const showDeletedParam = softDeleteFilter === 'deleted' ? 'deleted' : 
                             softDeleteFilter === 'all' ? 'all' : undefined;

      const result = await api.getAdminBehaviors({
        page: pagination.pageIndex + 1,
        pageSize: pagination.pageSize,
        sortBy: sorting[0]?.id || 'createdAt',
        sortOrder: sorting[0]?.desc ? 'desc' : 'asc',
        familyNameFilter,
        childNameFilter,
        notesFilter,
        showDeleted: showDeletedParam
      });

      setData(result.behaviors);
      setTotalCount(result.totalCount);
    } catch (err) {
      console.error('Error fetching behaviors:', err);
      setError('Error loading behaviors. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [pagination, sorting, columnFilters, softDeleteFilter]);

  useEffect(() => {
    fetchBehaviors();
  }, [fetchBehaviors]);

  const handleEdit = (behavior: AdminBehaviorRecord) => {
    setSelectedBehavior(behavior);
    setEditData({
      behaviorType: behavior.behaviorType,
      notes: behavior.notes || '',
    });
    setEditModalOpen(true);
  };

  const handleDelete = (behavior: AdminBehaviorRecord) => {
    setSelectedBehavior(behavior);
    setDeleteDialogOpen(true);
  };

  const handleRestore = async (_behavior: AdminBehaviorRecord) => {
    try {
      // For now, we'll implement restore by calling the restore endpoint when it's available
      // Since there's no specific restore endpoint in the current API, we'll update this later
      // TODO: Implement restore endpoint call when backend provides it
    } catch (err) {
      console.error('Error restoring behavior:', err);
      setError('Failed to restore behavior. Please try again.');
    }
  };

  const handleEditSave = async () => {
    if (!selectedBehavior) return;
    
    try {
      await api.adminAPI.updateAdminBehavior(selectedBehavior.behaviorId, {
        behaviorType: editData.behaviorType,
        notes: editData.notes,
        childId: selectedBehavior.childId,
      });
      setEditModalOpen(false);
      fetchBehaviors();
    } catch (err) {
      console.error('Error updating behavior:', err);
      setError('Failed to update behavior. Please try again.');
    }
  };

  const handleDeleteConfirm = async () => {
    if (!selectedBehavior) return;
    
    try {
      await api.adminAPI.deleteAdminBehavior(selectedBehavior.behaviorId);
      setDeleteDialogOpen(false);
      fetchBehaviors();
    } catch (err) {
      console.error('Error deleting behavior:', err);
      setError('Failed to delete behavior. Please try again.');
    }
  };

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      columnFilters,
      pagination,
    },
    pageCount: Math.ceil(totalCount / pagination.pageSize),
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: true,
    manualFiltering: true,
    manualSorting: true,
  });

  const renderMobileCard = (behavior: AdminBehaviorRecord) => {
    // Helper function to get the best display time (following EventLog pattern)
    const getDisplayTime = (behavior: AdminBehaviorRecord) => {
      // Use eventLocalTime first (what user saw), then fallback to createdAt
      return behavior.eventLocalTime || behavior.createdAt;
    };
    
    const displayTime = getDisplayTime(behavior);
    const isDeleted = behavior.isDeleted;
    const isGreyedOut = softDeleteFilter === 'all' && isDeleted;
    
    return (
      <Card 
        key={behavior.behaviorId} 
        sx={{ 
          mb: 2,
          opacity: isGreyedOut ? 0.5 : 1,
          backgroundColor: isGreyedOut ? theme.palette.grey[100] : undefined
        }}
      >
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
            <Typography 
              variant="subtitle1" 
              fontWeight="medium"
              sx={{ color: isGreyedOut ? theme.palette.grey[500] : 'inherit' }}
            >
              {behavior.childName} - {behavior.familyName}
              {isDeleted && (
                <Chip 
                  label="DELETED" 
                  size="small" 
                  color="error" 
                  variant="outlined"
                  sx={{ ml: 1, fontSize: '0.7rem', height: '20px' }}
                />
              )}
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <IconButton
                size="small"
                onClick={() => handleEdit(behavior)}
                data-testid="edit-behavior-button"
              >
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => isDeleted ? handleRestore(behavior) : handleDelete(behavior)}
                data-testid={isDeleted ? "restore-behavior-button" : "delete-behavior-button"}
                title={isDeleted ? "Restore behavior (Coming Soon)" : "Delete behavior"}
              >
                {isDeleted ? <RestoreIcon fontSize="small" /> : <DeleteIcon fontSize="small" />}
              </IconButton>
            </Box>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Chip
              icon={getBehaviorIcon(behavior.behaviorType)}
              label={getBehaviorLabel(behavior.behaviorType)}
              size="small"
              sx={{
                opacity: isGreyedOut ? 0.7 : 1,
                backgroundColor: getBehaviorColor(behavior.behaviorType),
                color: 'white',
                '& .MuiChip-label': { color: 'white' }
              }}
            />
            <Typography 
              variant="caption" 
              color="text.secondary"
              sx={{ color: isGreyedOut ? theme.palette.grey[500] : undefined }}
            >
              {(() => {
                // Safely format the date with NO timezone conversion - admin sees exact API values
                if (!displayTime) return 'No date';
                
                // Parse ISO datetime string directly without any Date object to avoid timezone conversion
                const match = displayTime.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/);
                if (match) {
                  const [, year, month, day, hour, minute] = match;
                  const hour24 = parseInt(hour);
                  const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
                  const ampm = hour24 >= 12 ? 'PM' : 'AM';
                  return `${month}/${day}/${year} ${hour12}:${minute} ${ampm}`;
                }
                
                // If no match, return the raw string to avoid any Date conversion
                return `Raw: ${displayTime}`;
              })()}
            </Typography>
          </Box>
          
          <Typography 
            variant="body2" 
            color="text.secondary"
            sx={{ color: isGreyedOut ? theme.palette.grey[500] : undefined }}
          >
            Logged by: {getRecordedByDisplayName(behavior)}
          </Typography>
          
          {behavior.notes && (
            <Typography 
              variant="body2" 
              sx={{
                mt: 1,
                color: isGreyedOut ? theme.palette.grey[500] : 'inherit'
              }}
            >
              {behavior.notes}
            </Typography>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={() => navigate('/admin')}
        >
          Back to Admin
        </Button>
        <Typography variant="h4" component="h1">
          Behavior List Manager
        </Typography>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Loading behaviors...</Typography>
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <>
          {!isMobile ? (
            <Paper sx={{ width: '100%', overflow: 'hidden' }} data-testid="admin-behavior-log-table">
              <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Showing {data.length} of {totalCount} behaviors
                </Typography>
                <SoftDeleteToggle
                  value={softDeleteFilter}
                  onChange={(newFilter) => {
                    setSoftDeleteFilter(newFilter);
                    setPagination(prev => ({ ...prev, pageIndex: 0 })); // Reset to first page
                  }}
                  disabled={loading}
                />
              </Box>
              
              <Box sx={{ overflowX: 'auto' }}>
                <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
                  <Box component="thead">
                    {table.getHeaderGroups().map(headerGroup => (
                      <Box component="tr" key={headerGroup.id}>
                        {headerGroup.headers.map(header => (
                          <Box
                            component="th"
                            key={header.id}
                            sx={{
                              padding: '12px 16px',
                              textAlign: 'left',
                              borderBottom: `2px solid ${theme.palette.grey[300]}`,
                              backgroundColor: theme.palette.grey[100],
                              fontWeight: 600,
                              cursor: header.column.getCanSort() ? 'pointer' : 'default',
                              userSelect: 'none',
                            }}
                            onClick={header.column.getToggleSortingHandler()}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                              {header.column.getCanSort() && (
                                <>
                                  {header.column.getIsSorted() === 'desc' && <ArrowDownward fontSize="small" />}
                                  {header.column.getIsSorted() === 'asc' && <ArrowUpward fontSize="small" />}
                                </>
                              )}
                            </Box>
                            {header.column.getCanFilter() && (
                              <Box sx={{ mt: 1 }}>
                                <TextField
                                  size="small"
                                  placeholder={(header.column.columnDef.meta as { filterPlaceholder?: string } | undefined)?.filterPlaceholder}
                                  value={(header.column.getFilterValue() ?? '') as string}
                                  onChange={e => header.column.setFilterValue(e.target.value)}
                                  onClick={e => e.stopPropagation()}
                                  // TextField input sizing, not a Typography override
                                  sx={{ fontSize: '0.875rem' }}
                                />
                              </Box>
                            )}
                          </Box>
                        ))}
                      </Box>
                    ))}
                  </Box>
                  <Box component="tbody">
                    {table.getRowModel().rows.length === 0 ? (
                      <Box component="tr">
                        <Box component="td" colSpan={columns.length} sx={{ textAlign: 'center', padding: '48px' }}>
                          <Typography color="text.secondary">No behaviors found</Typography>
                        </Box>
                      </Box>
                    ) : (
                      table.getRowModel().rows.map((row, index) => {
                        const isDeleted = row.original.isDeleted;
                        const isGreyedOut = softDeleteFilter === 'all' && isDeleted;

                        return (
                          <Box
                            component="tr"
                            key={row.id}
                            sx={{
                              backgroundColor: index % 2 === 0 ? theme.palette.common.white : theme.palette.grey[100],
                              opacity: isGreyedOut ? 0.5 : 1,
                              color: isGreyedOut ? theme.palette.grey[500] : 'inherit'
                            }}
                          >
                          {row.getVisibleCells().map(cell => (
                            <Box
                              component="td"
                              key={cell.id}
                              sx={{
                                padding: '12px 16px',
                                borderBottom: `1px solid ${theme.palette.grey[300]}`,
                                color: isGreyedOut ? theme.palette.grey[500] : 'inherit'
                              }}
                            >
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )}
                            </Box>
                          ))}
                        </Box>
                        );
                      })
                    )}
                  </Box>
                </Box>
              </Box>

              <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    size="small"
                    onClick={() => table.setPageIndex(0)}
                    disabled={!table.getCanPreviousPage()}
                  >
                    First
                  </Button>
                  <Button
                    size="small"
                    onClick={() => table.previousPage()}
                    disabled={!table.getCanPreviousPage()}
                  >
                    Previous
                  </Button>
                  <Button
                    size="small"
                    onClick={() => table.nextPage()}
                    disabled={!table.getCanNextPage()}
                  >
                    Next
                  </Button>
                  <Button
                    size="small"
                    onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                    disabled={!table.getCanNextPage()}
                  >
                    Last
                  </Button>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="body2">
                    Page {pagination.pageIndex + 1} of {table.getPageCount()}
                  </Typography>
                  <select
                    value={pagination.pageSize}
                    onChange={e => {
                      table.setPageSize(Number(e.target.value));
                    }}
                  >
                    {[10, 25, 50, 100].map(pageSize => (
                      <option key={pageSize} value={pageSize}>
                        Show {pageSize}
                      </option>
                    ))}
                  </select>
                </Box>
              </Box>
            </Paper>
          ) : (
            <Box data-testid="mobile-behavior-cards">
              <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Showing {data.length} of {totalCount} behaviors
                  </Typography>
                  <SoftDeleteToggle
                    value={softDeleteFilter}
                    onChange={(newFilter) => {
                      setSoftDeleteFilter(newFilter);
                      setPagination(prev => ({ ...prev, pageIndex: 0 })); // Reset to first page
                    }}
                    disabled={loading}
                  />
                </Box>
                <TextField
                  size="small"
                  placeholder="Search child name..."
                  value={(columnFilters.find(f => f.id === 'childName')?.value ?? '') as string}
                  onChange={e => {
                    setColumnFilters(prev => [
                      ...prev.filter(f => f.id !== 'childName'),
                      { id: 'childName', value: e.target.value }
                    ]);
                  }}
                />
                <TextField
                  size="small"
                  placeholder="Search family name..."
                  value={(columnFilters.find(f => f.id === 'familyName')?.value ?? '') as string}
                  onChange={e => {
                    setColumnFilters(prev => [
                      ...prev.filter(f => f.id !== 'familyName'),
                      { id: 'familyName', value: e.target.value }
                    ]);
                  }}
                />
                <TextField
                  size="small"
                  placeholder="Search notes..."
                  value={(columnFilters.find(f => f.id === 'notes')?.value ?? '') as string}
                  onChange={e => {
                    setColumnFilters(prev => [
                      ...prev.filter(f => f.id !== 'notes'),
                      { id: 'notes', value: e.target.value }
                    ]);
                  }}
                />
              </Box>

              {data.length === 0 ? (
                <Card>
                  <CardContent sx={{ textAlign: 'center', py: 6 }}>
                    <Typography color="text.secondary">No behaviors found</Typography>
                  </CardContent>
                </Card>
              ) : (
                data.map(behavior => renderMobileCard(behavior))
              )}

              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 1 }}>
                <Button
                  size="small"
                  onClick={() => setPagination(prev => ({ ...prev, pageIndex: prev.pageIndex - 1 }))}
                  disabled={pagination.pageIndex === 0}
                >
                  Previous
                </Button>
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                  Page {pagination.pageIndex + 1} of {Math.ceil(totalCount / pagination.pageSize)}
                </Typography>
                <Button
                  size="small"
                  onClick={() => setPagination(prev => ({ ...prev, pageIndex: prev.pageIndex + 1 }))}
                  disabled={pagination.pageIndex >= Math.ceil(totalCount / pagination.pageSize) - 1}
                >
                  Next
                </Button>
              </Box>
            </Box>
          )}
        </>
      )}

      {/* Edit Modal */}
      <Dialog 
        open={editModalOpen} 
        onClose={() => setEditModalOpen(false)}
        maxWidth="sm"
        fullWidth
        data-testid="admin-behavior-edit-modal"
      >
        <DialogTitle>Edit Behavior Record</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Child: {selectedBehavior?.childName} ({selectedBehavior?.familyName})
            </Typography>
            
            <FormControl fullWidth>
              <InputLabel>Behavior Type</InputLabel>
              <Select
                value={editData.behaviorType}
                onChange={(e) => setEditData(prev => ({ ...prev, behaviorType: Number(e.target.value) }))}
                label="Behavior Type"
              >
                <MenuItem value={BehaviorType.Star}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {getBehaviorIcon(BehaviorType.Star, { sx: { mr: 1 } })}
                    {getBehaviorLabel(BehaviorType.Star)}
                  </Box>
                </MenuItem>
                <MenuItem value={BehaviorType.Triangle}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {getBehaviorIcon(BehaviorType.Triangle, { sx: { mr: 1 } })}
                    {getBehaviorLabel(BehaviorType.Triangle)}
                  </Box>
                </MenuItem>
                <MenuItem value={BehaviorType.Square}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {getBehaviorIcon(BehaviorType.Square, { sx: { mr: 1 } })}
                    {getBehaviorLabel(BehaviorType.Square)}
                  </Box>
                </MenuItem>
                <MenuItem value={BehaviorType.FlipYourCoin}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {getBehaviorIcon(BehaviorType.FlipYourCoin, { sx: { mr: 1 } })}
                    {getBehaviorLabel(BehaviorType.FlipYourCoin)}
                  </Box>
                </MenuItem>
                <MenuItem value={BehaviorType.MakeItRight}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {getBehaviorIcon(BehaviorType.MakeItRight, { sx: { mr: 1 } })}
                    {getBehaviorLabel(BehaviorType.MakeItRight)}
                  </Box>
                </MenuItem>
                <MenuItem value={BehaviorType.OneOnOneTime}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {getBehaviorIcon(BehaviorType.OneOnOneTime, { sx: { mr: 1 } })}
                    {getBehaviorLabel(BehaviorType.OneOnOneTime)}
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Notes"
              multiline
              rows={3}
              value={editData.notes}
              onChange={(e) => setEditData(prev => ({ ...prev, notes: e.target.value }))}
            />

            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateTimePicker
                label="Event Time (optional)"
                disabled
                slotProps={{
                  textField: {
                    fullWidth: true,
                    helperText: "Event time is managed by the enhanced time system"
                  }
                }}
              />
            </LocalizationProvider>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditModalOpen(false)}>Cancel</Button>
          <Button onClick={handleEditSave} variant="contained">Save</Button>
        </DialogActions>
        <Box data-testid="admin-permissions-enabled behavior-edit-modal" sx={{ display: 'none' }} />
      </Dialog>

      {/* Delete Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        data-testid="behavior-delete-dialog"
      >
        <DialogTitle>Delete Behavior Record</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this behavior record for {selectedBehavior?.childName}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminBehaviorManagement;